import type { FloorIdT } from '@/content/schema';

export interface Appearance { height: 'short' | 'mid' | 'tall'; skin: number; face: 'smile' | 'calm' | 'determined' | 'sleepy'; shirt: string; hair: string }

/** 跨天保存的玩家紀錄（不受回溯影響） */
export interface Profile {
  version: number;
  day: number;
  roleId: string | null;
  cleared: Record<string, { grade: 'good' | 'bad'; best: number; total: number; day: number }>;
  hazardsSeen: string[];
  eventsLearned: string[];
  items: string[];          // 永久道具（護身符、吊牌、乖乖、鑰匙）
  endings: string[];
  goodTotal: number;
  settings: { sound: boolean; guide: boolean; tasksOpen: boolean };
  // V3
  counters: Record<string, number>;
  flags: Record<string, boolean>;
  achievements: Record<string, number>;   // id → 解鎖的天數
  challenges: string[];                   // 今日挑戰（讀取代碼時會重抽）
  productsSeen: string[];
  knowledge: string[];                    // 已解鎖的部門流程（成品食譜）
  cadresTalked: string[];
  deptsPlayed: string[];
  appearance: Appearance;
  codes: Record<string, { label: string; day: number; clock: number; role: string; savedAt: number }>;
  // v0.4
  /** 做完的「一輩子一次」任務（支線、特殊） */
  missionsDone: string[];
  // v0.6 成長系統
  xp: number;
  /** 福利點（福利社換東西） */
  points: number;
  /** 月薪（底薪＋升級＋抓老鼠加薪） */
  salary: number;
  raises: { day: number; amount: number; why: string }[];
  /** 福利社買的東西：id → 數量 */
  stash: Record<string, number>;
  /** 收集到的成品食譜（content/recipes/menu.json 的 id） */
  recipes: string[];
  /** 聽過的小知識（content/tips.json 的 id） */
  tipsHeard: string[];
  /** 連續上班天數 */
  streak: number;
}

export type CaseStage = 'pending' | 'talk' | 'inspect' | 'report' | 'done';
export interface CaseProgress {
  stage: CaseStage; round: number; best: number; picks: number[];
  stopped: boolean; grade?: 'good' | 'bad'; introSeen: boolean;
}
export interface MissionProgress { step: number; done: boolean; good: number; bad: number; /** v1.0 任務失敗（例如熟食排籃數量錯了） */ failed?: boolean }
export interface Injury { label: string; severity: 'light' | 'major' | 'fatal'; at: number; source: string }
export interface OrderLine { product: string; forecast: number; target: number; produced: number; suspect: number; scrapped: number }
/** 烏龜車：load 是任務裝上去的「一疊料」，baskets 是玩家自己放上去的籃子（籃子 id） */
export interface CartState { floor: FloorIdT; pos: [number, number]; load: { material: string; baskets: number; kg: number } | null; baskets: string[];
  /** v0.8 線邊物流：ship＝成品車（arrived 在 1F 成品電梯口、queued 在出貨區排隊）；since＝從什麼時候開始 */
  tag?: 'ship'; stage?: 'arrived' | 'queued'; since?: number; from?: string }
/** 籃子：on＝null 放在地上的墊底籃／架子上、'hand' 拿在手上、其他＝放在哪台烏龜車上 */
/** 籃子：on＝null 放在架子／產線旁、'hand' 拿在手上、'bk:<籃子 id>' 疊在另一個籃子上、其他＝放在哪台烏龜車上 */
export interface BasketState { floor: FloorIdT; pos: [number, number]; content: string; on: string | null; used?: boolean;
  /** v0.8：熟食用籃（2F 熟食洗籃區洗）／生食・一般用籃（1F 洗籃區洗） */
  cooked?: boolean;
  /** v0.8：放在桌子、架子上的高度（沒有＝地上的那一疊，下面一定是烏龜車或墊底籃） */
  y?: number }
/** v0.8 線邊物流的狀態（content/flow.json） */
export interface FlowState {
  seq: number;
  /** 每一站：made＝還沒裝滿一籃的數量、last＝上次看到的產量、stall＝停下來的原因（empty 缺空籃／full 收料車滿了） */
  st: Record<string, { made: number; last: number; stall: null | 'empty' | 'full'; stallAt: number; baskets: number; next: number; warned?: boolean }>;
  /** 生產部會遇到的「線邊缺空籃」 */
  shortages: { station: string; at: number; state: 'pending' | 'active' | 'done' | 'rescued'; since: number }[];
  washing: { washer: string; n: number; ready: number }[];
  /** 計時器：某個位置的車滿了／空了從什麼時候開始（key＝樓層@x,z） */
  timers: Record<string, number>;
  shipped: number;
  /** 玩家自己做的：送成品電梯、出貨、洗籃、送半成品、補空籃 */
  by: { sent: number; shipped: number; washed: number; semi: number; refill: number };
}
export interface GroundItem { uid: string; type: string; floor: FloorIdT; pos: [number, number] }

/** 單一班別的狀態（會被快照、可回溯、可轉成進度代碼） */
export interface DayState {
  version: number;
  day: number; roleId: string; rng: number;
  clock: number; shiftStart: number; shiftEnd: number;
  floor: FloorIdT; pos: [number, number]; zone: string;
  player: { hp: number; fatigue: number; hydration: number; focus: number; stress: number };
  factory: { foodSafety: number; records: number; trust: number; audit: number; fines: number;
    caught: number; unseen: number; reworkMinutes: number };
  injuries: Injury[];
  caseQueue: string[]; activeCase: number; cases: Record<string, CaseProgress>;
  missionQueue: string[]; missions: Record<string, MissionProgress>; missionStartedAt: Record<string, number>;
  orders: { official: boolean; lines: OrderLine[] };
  lines: Record<string, { stoppedBy: string | null; reworkUntil: number; alt: boolean; changeoverUntil: number; nextChange: number }>;
  events: { last: Record<string, number>; firedToday: string[]; ignored: number; inside: string[]; playSec: number; nextRandomAt: number };
  items: { held: string[]; guaiguai: { floor: FloorIdT; equipment: string } | null; spotted: boolean; freeRewindUsed: boolean };
  dressed: boolean;
  /** 氣浴後在洗手區洗過手（進生產區的門要檢查）；回到非生產區就失效 */
  washed: boolean;
  /** v1.1.1 洗手做到第幾步（0＝還沒洗、1＝洗好手、2＝酒精好了；烘手後 washed=true 歸 0） */
  washStep?: number;
  /** 上班打卡了沒（1F 廠區外的打卡鐘） */
  clockedIn: boolean;
  carts: Record<string, CartState>;
  pushing: string | null;
  /** 手上拿著的東西：籃子或拿起來的烏龜車 */
  holding: { kind: 'basket' | 'cart'; id: string } | null;
  baskets: Record<string, BasketState>;
  bag: string[];                 // 撿起來、還沒處理的東西
  /** v0.5 背包：任務給的工作用品（交接單、樣品袋、新人用品組……，pack-items.json 的 id） */
  pack: string[];
  /** v0.5 這一趟有沒有走過氣浴室進來（從進貨區、冰箱繞進來的不算）；回到非生產區就失效 */
  inProd: boolean;
  ground: GroundItem[];
  floorsVisited: string[];
  log: { at: number; text: string; kind: 'info' | 'good' | 'bad' | 'warn' }[];
  flags: Record<string, boolean>;
  ended: boolean;
  /** v0.6 今天賺到的：經驗、福利點、加薪、紀錄 */
  earn: { xp: number; points: number; raise: number; notes: string[] };
  /** v0.6 現在出現的老鼠（一次最多一隻） */
  rat: { floor: FloorIdT; pos: [number, number]; until: number; zone: string } | null;
  /** v0.6 貨架上今天被拿走幾個：'貨架 id:物品 id' → 數量 */
  shelfTaken: Record<string, number>;
  /** v0.7：貨架放大時用拖拉排過的位置（每格一個物品 id 或空格 null） */
  shelfLayout?: Record<string, (string | null)[]>;
  /** v1.0 模式：work＝正式上班；intern＝自由模式（實習生：沒有主線，只有支線） */
  mode?: 'work' | 'intern';
  /** v1.1 玩家選定（或剛接下）的任務：導航、「!」標示跟著它 */
  focus?: string;
  /** v1.1 巡場來訪：npc id → 現在在哪一層、待到什麼時候、下一次什麼時候來（events.playSec 秒） */
  visits?: Record<string, { floor: string | null; until: number; next: number; count: number }>;
  /** v1.1 今天已經重新挑戰過的任務（一天一次） */
  retried?: string[];
  /** v1.0 這一天有沒有選新手教學 */
  tutorial?: boolean;
  /** v0.8 線邊物流（沒有＝舊存檔，第一次 tick 會補上） */
  flow?: FlowState;
  /** v1.3 今天看過的溫度計：spot → 讀數（°C） */
  temps?: Record<string, number>;
  /** v1.3「作業在哪裡」按「帶我去」：導航先帶到這裡（走到旁邊就清掉） */
  waypoint?: { floor: FloorIdT; target: string; label: string } | null;
  /** v0.6 今天聊過天的作業員（actor id） */
  chatted: string[];
}

/** 回溯用的檢查點（不含 profile；重生次數另記） */
export interface Checkpoint { label: string; state: DayState; reopenEvent?: string; code: string }
export interface Meta { rewinds: number; checkpoints: Checkpoint[] }

/** 新人黃圈帽戴多久（遊戲天數；三個月） */
export const NEWBIE_DAYS = 90;

export const PROFILE_VERSION = 5;
export const DAY_VERSION = 5;

export const defaultAppearance = (): Appearance => ({ height: 'mid', skin: 0, face: 'smile', shirt: '#e07b5a', hair: '#2b2220' });

export function newProfile(): Profile {
  return { version: PROFILE_VERSION, day: 1, roleId: null, cleared: {}, hazardsSeen: [], eventsLearned: [],
    items: [], endings: [], goodTotal: 0, settings: { sound: true, guide: true, tasksOpen: true },
    counters: {}, flags: {}, achievements: {}, challenges: [], productsSeen: [], knowledge: [], cadresTalked: [], deptsPlayed: [],
    appearance: defaultAppearance(), codes: {}, missionsDone: [],
    xp: 0, points: 0, salary: 29500, raises: [], stash: {}, recipes: [], tipsHeard: [], streak: 0 };
}

export const clone = <T>(x: T): T => (typeof structuredClone === 'function' ? structuredClone(x) : JSON.parse(JSON.stringify(x)));
export const clamp = (v: number, a = 0, b = 100) => Math.max(a, Math.min(b, v));
